import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { Assignee } from 'infrastructure/enums';
import { mainlandTimezonesMapping } from 'infrastructure/data-sources/timezones';

import { getAssignToolQueryKey } from './use-get-assign-tool';
import {
  getRpmOverviewDashboardQueryKey,
  PAGE_SIZE,
  useOverviewDashboardFilter,
} from '../overview-dashboard';

import type {
  RpmOverviewDashboardResponse,
  TDataFilter,
} from '../overview-dashboard';
import type { ISetAssignPatientResponse } from 'infrastructure/interfaces';

const url = 'reports/reports/interactive-time-dashboard-assign';

const defaultData = {
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
};

export type TDurationInMinutes = 60 | 120 | 180;

interface IDataProps {
  patientGuid: string;
  action: 'assign' | 'unassign';
  durationInMinutes?: TDurationInMinutes;
}

const mutationFn = async (dataProps: IDataProps) => {
  const { data, error } =
    await dataService.updateOnly<ISetAssignPatientResponse>(url, {
      ...dataProps,
      ...defaultData,
    });

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useAssignPatient = () => {
  const queryClient = useQueryClient();
  const { filter } = useOverviewDashboardFilter();
  const { startTime, endTime, status, ...filterWithoutDates } = filter;
  const overviewQueryKeyFilter: TDataFilter = {
    ...filterWithoutDates,
    assignee: filter.assignee === Assignee.All ? undefined : filter.assignee,
    items: PAGE_SIZE,
    clinicGuid: filter.parentClinicGuid || filter.clinicGuid,
    childClinic: Boolean(filter.parentClinicGuid) || undefined,
    parentClinicGuid: undefined,
    year: endTime ? new Date(endTime).getFullYear() : undefined,
    month: endTime ? new Date(endTime).getMonth() + 1 : undefined,
    latestReadingStartTime: startTime,
    latestReadingEndTime: endTime,
    readingStatus: status,
    latestReading: filter.sorting,
    parentClinicOption:
      filter.parentClinicOption && filter.clinicGuid
        ? filter.parentClinicOption
        : undefined,
    timezone: filter.timezone
      ? mainlandTimezonesMapping[filter.timezone]
      : undefined,
  };

  const { mutate, isLoading, error } = useMutation<
    ISetAssignPatientResponse | undefined,
    string | undefined,
    IDataProps
  >({
    mutationFn,
    async onSuccess(data) {
      const assignToolQueryKey = getAssignToolQueryKey();
      await queryClient.invalidateQueries(assignToolQueryKey);
      showResult();

      const rpmOverviewDashboardQueryKey = getRpmOverviewDashboardQueryKey(
        overviewQueryKeyFilter,
      );
      queryClient.setQueryData<RpmOverviewDashboardResponse>(
        rpmOverviewDashboardQueryKey,
        (queryData) => {
          if (!queryData) return queryData;

          const newData = structuredClone(queryData);
          newData.data.forEach((item) => {
            if (item.guid === data?.data.patientGuid) {
              item.assignee = data?.data.assignee;
            }
          });

          return newData;
        },
      );
    },
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { assignPatient: mutate, isLoading };
};
